import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Video, CreditCard, X, CalendarClock } from "lucide-react";
import { api, errMsg, eur, fmtDate, KIND_LABELS, TIME_BANDS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { SlotPicker } from "@/components/panels/BookingFlow";

function Reschedule({ a, onDone }) {
  const [date, setDate] = useState(a.date);
  const [time, setTime] = useState(null);
  const [band, setBand] = useState(a.time_band);
  const save = async () => {
    try {
      await api.post(`/appointments/${a.id}/reschedule`, { date, time, time_band: band });
      toast.success("Appuntamento modificato");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-3 space-y-2 rounded-xl bg-slate-50 p-3">
      <Input type="date" data-testid={`reschedule-date-${a.id}`} value={date} onChange={(e) => setDate(e.target.value)} />
      {a.kind === "domicilio" ? (
        <div className="flex gap-2">{Object.entries(TIME_BANDS).map(([k, v]) => <button key={k} onClick={() => setBand(k)} className={`rounded-lg border px-2 py-1 text-xs ${band === k ? "border-sky-600 bg-sky-600 text-white" : "bg-white"}`}>{v}</button>)}</div>
      ) : <SlotPicker proId={a.professional_id} date={date} value={time} onChange={setTime} />}
      <Button size="sm" data-testid={`reschedule-save-${a.id}`} onClick={save} className="rounded-full bg-sky-600 hover:bg-sky-700">Salva modifica</Button>
    </div>
  );
}

export function AppointmentList({ items, reload }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const pay = async (a) => {
    try {
      const { data } = await api.post(`/payments/checkout/${a.id}`);
      navigate(`/pagamento/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async (a) => {
    await api.post(`/appointments/${a.id}/cancel`);
    toast.success("Appuntamento annullato");
    reload();
  };
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="appointments-empty">Nessun appuntamento.</p>;
  return (
    <div className="space-y-2" data-testid="appointments-list">
      {items.map((a) => (
        <div key={a.id} data-testid={`appointment-item-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-700">{KIND_LABELS[a.kind]}</p>
              <p className="text-sm font-semibold text-slate-800">{a.service_name}</p>
              <p className="text-xs text-slate-500">{a.professional_name} · {fmtDate(a.date)} {a.time || TIME_BANDS[a.time_band]} {a.city ? `· ${a.city}` : ""} · {eur(a.amount)}</p>
            </div>
            <StatusBadge status={a.status} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {a.status === "in_attesa_pagamento" && <Button size="sm" data-testid={`appointment-pay-${a.id}`} onClick={() => pay(a)} className="rounded-full bg-sky-600 hover:bg-sky-700"><CreditCard className="mr-1.5 h-3.5 w-3.5" /> Paga</Button>}
            {a.compensation?.status === "proposta" && <Button size="sm" data-testid={`appointment-compensation-${a.id}`} onClick={() => navigate(`/compenso/${a.id}`)} className="rounded-full bg-amber-500 text-amber-950 hover:bg-amber-400">Scegli rimborso o sconto</Button>}
            {a.kind === "video" && a.status === "confermato" && <Button size="sm" data-testid={`appointment-join-${a.id}`} onClick={() => navigate(`/video/${a.id}`)} className="rounded-full bg-cyan-600 hover:bg-cyan-700"><Video className="mr-1.5 h-3.5 w-3.5" /> Entra nella stanza</Button>}
            {!["annullato", "completato", "rifiutato"].includes(a.status) && (
              <>
                <Button size="sm" variant="outline" data-testid={`appointment-reschedule-${a.id}`} onClick={() => setOpen(open === a.id ? null : a.id)} className="rounded-full"><CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Modifica</Button>
                <Button size="sm" variant="outline" data-testid={`appointment-cancel-${a.id}`} onClick={() => cancel(a)} className="rounded-full text-rose-600"><X className="mr-1.5 h-3.5 w-3.5" /> Annulla</Button>
              </>
            )}
          </div>
          {open === a.id && <Reschedule a={a} onDone={() => { setOpen(null); reload(); }} />}
        </div>
      ))}
    </div>
  );
}
